/**
 * Phone Number Utilities
 * Pure functions for phone number formatting and validation
 */

/**
 * Format a phone number for display (e.g., "(555) 123-4567")
 */
export function formatPhoneForDisplay(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  
  // Remove country code if present
  const digits = cleaned.startsWith('1') && cleaned.length === 11 
    ? cleaned.slice(1) 
    : cleaned;
  
  if (digits.length !== 10) {
    return phone; // Return original if not valid
  }
  
  const area = digits.slice(0, 3);
  const prefix = digits.slice(3, 6);
  const line = digits.slice(6, 10);
  
  return `(${area}) ${prefix}-${line}`;
}

/**
 * Format a phone number to E.164 format (e.g., "+15551234567")
 * Assumes US numbers if no country code provided
 */
export function formatPhoneE164(phone: string, defaultCountryCode: string = '1'): string {
  const cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.length === 10) {
    return `+${defaultCountryCode}${cleaned}`;
  }
  
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }
  
  // Already has full international format
  if (cleaned.length > 10) {
    return `+${cleaned}`;
  }
  
  return phone; // Return original if can't parse
}

/**
 * Validate a phone number (basic validation)
 * Returns true if the number could be valid
 */
export function isValidPhone(phone: string): boolean {
  const cleaned = phone.replace(/\D/g, '');
  
  // Must be 10 digits (US) or 11 digits starting with 1
  if (cleaned.length === 10) return true;
  if (cleaned.length === 11 && cleaned.startsWith('1')) return true;
  
  // Allow international numbers (11+ digits)
  if (cleaned.length >= 11) return true;
  
  return false;
}

/**
 * Check if a phone number is a fake test number (555 exchange pattern)
 * Fake numbers have 555 as the exchange (middle 3 digits): XXX-555-XXXX
 * Also catches 555-XXX-XXXX where 555 is the area code
 */
export function isFakeTestNumber(phone: string): boolean {
  const cleaned = phone.replace(/\D/g, '');
  
  // Remove country code if present
  const digits = cleaned.startsWith('1') && cleaned.length === 11 
    ? cleaned.slice(1) 
    : cleaned;
  
  if (digits.length !== 10) return false;
  
  // Check if exchange (digits 3-5) is 555
  const exchange = digits.slice(3, 6);
  return exchange === '555';
}

/**
 * Extract area code from phone number
 */
export function getAreaCode(phone: string): string | null {
  const cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.length === 10) {
    return cleaned.slice(0, 3);
  }
  
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return cleaned.slice(1, 4);
  }
  
  return null;
}

/**
 * Mask a phone number for privacy (e.g., "***-***-4567")
 */
export function maskPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  
  // Get last 4 digits
  const lastFour = cleaned.slice(-4);
  
  if (cleaned.length >= 10) {
    return `***-***-${lastFour}`;
  }
  
  return `***-${lastFour}`;
}

/**
 * Format phone for Twilio (ensure E.164 format)
 */
export function formatPhoneForTwilio(phone: string): string {
  return formatPhoneE164(phone);
}

/**
 * Parse user input into a standardized phone format
 * Handles various input formats users might enter
 */
export function parsePhoneInput(input: string): {
  isValid: boolean;
  formatted: string;
  e164: string;
  display: string;
} {
  const cleaned = input.replace(/\D/g, '');
  const isValid = isValidPhone(input);
  
  return {
    isValid,
    formatted: cleaned,
    e164: formatPhoneE164(input),
    display: formatPhoneForDisplay(input),
  };
}

/**
 * Compare two phone numbers for equality (ignoring formatting)
 */
export function phonesMatch(phone1: string, phone2: string): boolean {
  const e164_1 = formatPhoneE164(phone1);
  const e164_2 = formatPhoneE164(phone2);
  return e164_1 === e164_2;
}

/**
 * Get a clickable tel: URL for a phone number
 */
export function getPhoneUrl(phone: string): string {
  return `tel:${formatPhoneE164(phone)}`;
}

/**
 * Get a clickable sms: URL for a phone number
 */
export function getSmsUrl(phone: string, body?: string): string {
  const e164 = formatPhoneE164(phone);
  if (body) {
    return `sms:${e164}?body=${encodeURIComponent(body)}`;
  }
  return `sms:${e164}`;
}
